// OwnerByokNudgeModal — fires when a public-silo /ask request returns
// HTTP 402 with detail.code === "OWNER_BYOK_NOT_SET". Tells the visitor
// the owner hasn't connected their AI engine yet, and offers a one-tap
// "Send a nudge" button that fires POST /silos/notify-owner-byok to
// email the silo owner.
//
// Sprint Feb 8, 2026 — replaces the previous brutal toast that told
// VISITORS to set up a Gemini key (which made no sense — they're not
// the silo owner).
import { useState } from "react";
import { X, EnvelopeSimple, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function OwnerByokNudgeModal({ open, onClose, siloSlug, siloOwnerName }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/silos/notify-owner-byok", {
        silo_slug: siloSlug,
        visitor_email: email.trim() || null,
      });
      if (r.data.sent) {
        toast.success(`Nudge sent to ${siloOwnerName}.`);
        onClose();
      } else if (r.data.reason === "rate_limited") {
        toast.message(r.data.message || "Already nudged in the last 12h — give them a moment.");
        onClose();
      } else if (r.data.reason === "already_connected") {
        toast.success("Looks like they just got set up — try asking again.");
        onClose();
      } else {
        toast.error("Couldn't send the nudge. Try again in a bit.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the nudge.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="owner-byok-nudge-modal"
      onClick={onClose}
    >
      <div className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C16E5A] flex items-center gap-1.5">
            <Sparkle size={10} weight="duotone" /> Almost there
          </div>
          <button onClick={onClose} data-testid="owner-byok-modal-close" className="text-[#6B6A68] hover:text-[#1C1B1A] text-xl leading-none -mt-1">×</button>
        </div>
        <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
          {siloOwnerName} hasn't connected their AI engine yet
        </h3>
        <p className="text-sm text-[#6B6A68] mt-3 leading-relaxed">
          That's why this silo isn't answering — every silo on My Silo runs on its owner's own AI key, and {siloOwnerName} hasn't set theirs up. Want to send them a quick nudge? They'll get a 3-minute setup walkthrough.
        </p>

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] block mb-1.5">Your email <span className="opacity-60">(optional)</span></label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="So they know who was asking"
            data-testid="owner-byok-modal-email"
            className="w-full px-3 py-2 text-sm rounded-md border hairline focus:outline-none focus:border-[#4A5D4E]"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="owner-byok-modal-cancel"
            className="text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            Maybe later
          </button>
          <button
            onClick={send}
            disabled={sending}
            data-testid="owner-byok-modal-send"
            className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-60"
          >
            <EnvelopeSimple size={13} weight="bold" />
            {sending ? "Sending…" : `Send a nudge to ${siloOwnerName}`}
          </button>
        </div>

        <p className="text-[10px] text-[#6B6A68] mt-4 leading-relaxed">
          Rate-limited to one nudge per silo every 12 hours.
        </p>
      </div>
    </div>
  );
}
